import {useQuery} from '@tanstack/react-query'
import {Strong} from '@/components/shared/Strong'
import {InlineRow} from '@/components/shared/InlineRow'
import {Users, Server, Shield, TrendingUp} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {Card, CardContent, CardHeader, CardTitle, CardDescription} from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {DatePicker} from '@/components/shared/DatePicker'
import {Label} from '@/components/ui/label'
import {PageLayout} from '@/components/shared/PageLayout'
import {MutedText} from '@/components/shared/MutedText'
import {IconText} from '@/components/shared/IconText'
import {Icon} from '@/components/shared/Icon'
import {TextLink} from '@/components/shared/TextLink'
import {ClickableCard} from '@/components/shared/ClickableCard'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {StatValue} from '@/components/shared/StatValue'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {routes} from '@/lib/routes'
import {useTimeRange, PeriodOption} from '@/hooks/use-time-range'

export function AccessAnalyticsDashboard() {
    const i18n = useI18n()
    const services = useServices()
    const {
        period,
        customFrom,
        customTo,
        handlePeriodChange,
        handleCustomRangeChange,
    } = useTimeRange('24h')


    // Fetch dashboard stats
    const {data: stats, isLoading, error} = useQuery({
        queryKey: ['effective-access-stats', period, customFrom, customTo],
        queryFn: () => services.analytics.dashboardStats(),
        refetchInterval: 30000,
    })

    const getPeriodLabel = () => {
        switch (period) {
            case PeriodOption.LAST_24H:
                return i18n.analytics.last24Hours
            case PeriodOption.LAST_7D:
                return i18n.analytics.last7Days
            case PeriodOption.LAST_30D:
                return i18n.analytics.last30Days
            case PeriodOption.CUSTOM:
                return i18n.analytics.customRange
            case PeriodOption.ALL:
                return i18n.backgroundJobs.allTime
            default:
                return period
        }
    }

    return (
        <PageLayout
            data-testid="access-analytics-dashboard-page"
            variant="dashboard"
            icon={CONCEPT_ICON.analyticsDashboard}
            loading={isLoading}
            error={error}
            title={i18n.analytics.effectiveAccessDashboard}
            description={i18n.analytics.effectiveAccessOverview}
            actions={
                <Select value={period} onValueChange={handlePeriodChange}>
                    <SelectTrigger className="w-[180px]" data-testid="filter-period">
                        <SelectValue placeholder={i18n.analytics.selectPeriod as string}/>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="24h">{i18n.analytics.last24Hours}</SelectItem>
                        <SelectItem value="7d">{i18n.analytics.last7Days}</SelectItem>
                        <SelectItem value="30d">{i18n.analytics.last30Days}</SelectItem>
                        <SelectItem value="custom">{i18n.analytics.customRange}</SelectItem>
                        <SelectItem value="all">{i18n.backgroundJobs.allTime}</SelectItem>
                    </SelectContent>
                </Select>
            }
        >
        <Stack gap="lg">
            {period === 'custom' && (
                <Card>
                    <CardContent className="pt-6">
                        <InlineRow gap="lg">
                            <InlineRow>
                                <Label htmlFor="from"><MutedText>{i18n.common.from}</MutedText></Label>
                                <DatePicker
                                    testId="input-analytics-from"
                                    withTime
                                    value={customFrom}
                                    onChange={(iso) => handleCustomRangeChange('from', iso)}
                                    ariaLabel={String(i18n.common.from)}
                                />
                            </InlineRow>
                            <InlineRow>
                                <Label htmlFor="to"><MutedText>{i18n.common.to}</MutedText></Label>
                                <DatePicker
                                    testId="input-analytics-to"
                                    withTime
                                    value={customTo}
                                    onChange={(iso) => handleCustomRangeChange('to', iso)}
                                    ariaLabel={String(i18n.common.to)}
                                />
                            </InlineRow>
                        </InlineRow>
                    </CardContent>
                </Card>
            )}

            {/* All-Time Totals */}
            <div>
                <Strong className="mb-4">{i18n.analytics.allTimeTotals}</Strong>
                <DetailGrid columns={4}>
                    <Card>
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <div>
                                    <MutedText>{i18n.analytics.totalGrants}</MutedText>
                                    <StatValue value={stats?.totalGrants || 0} variant="primary"/>
                                </div>
                                <Icon icon={Shield} size="xl" className="text-primary"/>
                            </InlineRow>
                        </CardContent>
                    </Card>
                    <ClickableCard to={routes.effectiveAccess.identities.list()} data-testid="link-identities-with-grants">
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <div>
                                    <MutedText>{i18n.analytics.identitiesWithGrants}</MutedText>
                                    <StatValue value={stats?.identitiesWithGrants || 0}/>
                                </div>
                                <Icon icon={Users} size="xl" className="text-status-info"/>
                            </InlineRow>
                        </CardContent>
                    </ClickableCard>
                    <ClickableCard to={routes.effectiveAccess.systems.list()} data-testid="link-target-systems">
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <div>
                                    <MutedText>{i18n.analytics.targetSystems}</MutedText>
                                    <StatValue value={stats?.targetSystemsWithGrants || 0} variant="success"/>
                                </div>
                                <Icon icon={Server} size="xl" className="text-status-success"/>
                            </InlineRow>
                        </CardContent>
                    </ClickableCard>
                    <Card>
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <div>
                                    <MutedText>{i18n.analytics.entitlementsGranted}</MutedText>
                                    <StatValue value={stats?.enforcementObjectsWithGrants || 0} variant="primary"/>
                                </div>
                                <Icon icon={Shield} size="xl" className="text-primary"/>
                            </InlineRow>
                        </CardContent>
                    </Card>
                </DetailGrid>
            </div>

            {/* Time Range Activity */}
            <div>
                <Strong className="mb-4">{i18n.analytics.activity} ({getPeriodLabel()})</Strong>
                <DetailGrid columns={3}>
                    <Card>
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <div>
                                    <MutedText>{i18n.analytics.grantsCreated}</MutedText>
                                    <StatValue value={stats?.grantsInRange || 0}/>
                                </div>
                                <Icon icon={TrendingUp} size="xl" className="text-status-warning"/>
                            </InlineRow>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <div>
                                    <MutedText>{i18n.analytics.identitiesAffected}</MutedText>
                                    <StatValue value={stats?.identitiesInRange || 0}/>
                                </div>
                                <Icon icon={Users} size="xl" className="text-status-info-muted"/>
                            </InlineRow>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <InlineRow justify="between">
                                <div>
                                    <MutedText>{i18n.analytics.systemsAffected}</MutedText>
                                    <StatValue value={stats?.systemsInRange || 0}/>
                                </div>
                                <Icon icon={Server} size="xl" className="text-status-success-muted"/>
                            </InlineRow>
                        </CardContent>
                    </Card>
                </DetailGrid>
            </div>

            {/* Top Lists */}
            <DetailGrid columns={2} gap="sm">
                {/* Top Identities */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg" data-testid="card-title-top-identities">
                            <IconText icon={Users} size="md">{i18n.analytics.topIdentitiesByGrants}</IconText>
                        </CardTitle>
                        <CardDescription>
                            {i18n.analytics.topIdentitiesByGrantsDesc}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {stats?.topIdentities && stats.topIdentities.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-identity">{i18n.analytics.identity}</TableHead>
                                        <TableHead className="text-right" data-testid="col-grants">{i18n.analytics.grants}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {stats.topIdentities.map((identity, index) => (
                                        <ClickableTableRow key={identity.id} to={routes.effectiveAccess.identities.detail(identity.id)}>
                                            <TableCell>
                                                <InlineRow>
                                                    <MutedText className="w-4">{index + 1}.</MutedText>
                                                    <TextLink
                                                        testId={`link-identity-detail-${identity.id}`}
                                                        to={routes.effectiveAccess.identities.detail(identity.id)}
                                                        variant="bold"
                                                        stopPropagation
                                                    >
                                                        {identity.name}
                                                    </TextLink>
                                                </InlineRow>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {identity.count}
                                            </TableCell>
                                        </ClickableTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <MutedText block centered className="py-4">
                                {i18n.analytics.noIdentitiesWithGrants}
                            </MutedText>
                        )}
                    </CardContent>
                </Card>

                {/* Top Systems */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg" data-testid="card-title-top-systems">
                            <IconText icon={Server} size="md">{i18n.analytics.topSystemsByGrants}</IconText>
                        </CardTitle>
                        <CardDescription>
                            {i18n.analytics.topSystemsByGrantsDesc}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {stats?.topSystems && stats.topSystems.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-system">{i18n.analytics.system}</TableHead>
                                        <TableHead className="text-right" data-testid="col-grants">{i18n.analytics.grants}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {stats.topSystems.map((system, index) => (
                                        <ClickableTableRow key={system.id} to={routes.effectiveAccess.systems.detail(system.id)}>
                                            <TableCell>
                                                <InlineRow>
                                                    <MutedText className="w-4">{index + 1}.</MutedText>
                                                    <TextLink
                                                        testId={`link-system-detail-${system.id}`}
                                                        to={routes.effectiveAccess.systems.detail(system.id)}
                                                        variant="bold"
                                                        stopPropagation
                                                    >
                                                        {system.name}
                                                    </TextLink>
                                                </InlineRow>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {system.count}
                                            </TableCell>
                                        </ClickableTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <MutedText block centered className="py-4">
                                {i18n.analytics.noSystemsWithGrants}
                            </MutedText>
                        )}
                    </CardContent>
                </Card>
            </DetailGrid>
        </Stack>
        </PageLayout>
    )
}
